import { AdvertErrorBoundary } from 'components/advert/advert-error-boundary.js';
import { shouldDisplayWithHideWhen } from 'layout/utils-hide-when.js';
import { ScreenSizeContext } from 'page-context/column-layout/column-layout-context.js';
import { CSSProperties, useContext, useRef } from 'react';
import { AdComponent } from 'redux-state/advertisement/types.js';
import { AdDomReadyContext } from 'routing/main-3-ads-ready-provider.js';
import { isNullOrUndefined } from 'utilities/isType.js';
import { BREAKPOINTS, isOneCol, SCREEN_SIZES } from 'utilities/media-queries.js';
import { AdvertComponent } from './advert-component.js';
import { AdvertiseWithMetService } from 'components-modules/advert/advertise-with-metservice';

const DEFAULT_HEADER_BANNER_HEIGHT = 250;

export type AdBannerProps = AdComponent<{
  // maxheight if the ad is smaller then will be smaller height
  height?: number;
  mobileHeight?: number;
  maxHeight?: number;
}>;

const getDefaultBannerHeight = (isMobile: boolean, banner?: AdBannerProps) => {
  const height = isMobile ? banner?.mobileHeight : banner?.height;
  if (isNullOrUndefined(height)) {
    return DEFAULT_HEADER_BANNER_HEIGHT; //250
  }
  return height;
};

const getBannerStyle = (isMobile: boolean, banner?: AdBannerProps): CSSProperties => {
  const minHeight = getDefaultBannerHeight(isMobile, banner);
  const maxHeight = banner?.maxHeight ?? minHeight;
  return { minHeight, maxHeight, height: '100%' };
};

/** METDEV-420 exclude certain top banner ad sizes based on viewport */
const adjustBannerSizesForScreen = (screenSize: SCREEN_SIZES, sizes: [number, number][]) => {
  return sizes.reduce<[number, number][]>((acc, size) => {
    const [width, _height] = size;

    if (width < BREAKPOINTS.LAPTOP) return [...acc, size];
    if (screenSize === SCREEN_SIZES.LAPTOP && width === BREAKPOINTS.LAPTOP) return [...acc, size];
    if (screenSize === SCREEN_SIZES.DESKTOP && width === BREAKPOINTS.DESKTOP) return [...acc, size];
    if (screenSize === SCREEN_SIZES.WIDESCREEN && width === BREAKPOINTS.WIDESCREEN) return [...acc, size];

    return acc;
  }, []);
};

export const BannerAd = ({ bannerConfig }: { bannerConfig?: AdBannerProps }) => {
  const screenSize = useContext(ScreenSizeContext);
  const bannerAdRef = useRef<HTMLElement>(null);

  if (!shouldDisplayWithHideWhen(bannerConfig?.layout, screenSize)) {
    return null;
  }

  /** We show hide banner now as we need the banner child to update when ads are ready to init */
  const showHeaderBanner = getDefaultBannerHeight(isOneCol(screenSize), bannerConfig) > 0;

  const sizeAdjustedBannerConfig: AdBannerProps | undefined = bannerConfig
    ? {
        ...bannerConfig,
        sizes: adjustBannerSizesForScreen(screenSize, bannerConfig.sizes),
      }
    : undefined;

  if (showHeaderBanner) {
    return (
      <header className="Header" ref={bannerAdRef} style={{ display: showHeaderBanner ? '' : 'none' }}>
        <BannerAdCore screenSize={screenSize} slotConfig={sizeAdjustedBannerConfig} />
      </header>
    );
  }
};

const BannerAdCore = ({ screenSize, slotConfig }: { screenSize: SCREEN_SIZES; slotConfig?: AdBannerProps }) => {
  const slotStyles = getBannerStyle(isOneCol(screenSize), slotConfig);
  const { bannerAd } = useContext(AdDomReadyContext);

  if (slotConfig) {
    return (
      <>
        <section className="BannerAd" style={slotStyles}>
          <AdvertErrorBoundary>
            <AdvertComponent {...slotConfig} origin="banner" key={screenSize} timerDOMReady={bannerAd.adDomReady} />
          </AdvertErrorBoundary>
        </section>
        {slotConfig?.showAdvertiseWithMS && (
          <div className="Container--blue">
            <AdvertiseWithMetService />
          </div>
        )}
      </>
    );
  }
  return null;
};
